var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { MoopsyError, MoopsyRawClientToServerMessageEventEnum, MoopsyRawServerToClientMessageEventEnum } from "@moopsyjs/core";
import { MoopsyClientExtensionBase } from "./base";
import React from "react";
import { createTimeout } from "../../lib/timeout";
import { TimeoutError } from "../errors/timeout-error";
import { isMoopsyError } from "../../lib/is-moopsy-error";
import { TransportStatus } from "../transports/base";
import { TypedEventEmitterV3 } from "@moopsyjs/toolkit";
import { MoopsyRequest } from "../request";
export var AuthExtensionStatus;
(function (AuthExtensionStatus) {
    AuthExtensionStatus["loggedIn"] = "loggedIn";
    AuthExtensionStatus["loggingIn"] = "loggingIn";
    AuthExtensionStatus["loggedOut"] = "loggedOut";
})(AuthExtensionStatus || (AuthExtensionStatus = {}));
export { AuthExtensionStatus as MoopsyClientAuthExtensionState }; // Backwards compatibility for pre 1.0.3
/**
 * MoopsyClientAuthExtension is provided as a built-in because of the way authentication is handled in Moopsy.
 */
export class MoopsyClientAuthExtension extends MoopsyClientExtensionBase {
    constructor(client, opts) {
        super(client);
        this.id = Math.random().toString();
        this.emitter = new TypedEventEmitterV3;
        this._isAttemptingAutoLogin = false;
        this.currentAuth = null;
        this._debug = (...args) => {
            this.client._debug("[AuthExtension]", ...args);
        };
        this.logout = () => {
            this.currentAuth = null;
            this.updateStatus(AuthExtensionStatus.loggedOut);
        };
        this.updateStatus = (status, data = null) => {
            this.status = status;
            this.emitter.emit(status, data);
            this._debug("Status updated to", status, data);
        };
        this.login = (params) => __awaiter(this, void 0, void 0, function* () {
            this.updateStatus(AuthExtensionStatus.loggingIn);
            void this.client.awaitConnected();
            const promise = new Promise((resolve, reject) => {
                const successHandler = () => {
                    resolve();
                    this.client.incomingMessageEmitter.off(MoopsyRawServerToClientMessageEventEnum.AUTH_SUCCESS, successHandler);
                };
                const failureHandler = (err) => {
                    const error = isMoopsyError(err) ? new MoopsyError(err.code, err.error, err.description) : new Error(err.message);
                    reject(error);
                    this.updateStatus(AuthExtensionStatus.loggedOut);
                    this.client.incomingMessageEmitter.off(MoopsyRawServerToClientMessageEventEnum.AUTH_ERROR, failureHandler);
                };
                this.client.incomingMessageEmitter.on(MoopsyRawServerToClientMessageEventEnum.AUTH_SUCCESS, successHandler);
                this.client.incomingMessageEmitter.on(MoopsyRawServerToClientMessageEventEnum.AUTH_ERROR, failureHandler);
            });
            this.client.send(new MoopsyRequest({
                event: MoopsyRawClientToServerMessageEventEnum.AUTH_LOGIN,
                data: params
            }, false, false));
            return promise;
        });
        this.handleLoginEvent = (auth) => {
            this.currentAuth = auth;
            this.updateStatus(AuthExtensionStatus.loggedIn, auth);
            this.client._emitter.emit("auth-extension/logged-in", undefined);
        };
        this.handleAutoLoginFailure = (err) => {
            this._debug("Auto login failed", err);
            this.emitter.emit("auto-login-failure", err);
            this.updateStatus(AuthExtensionStatus.loggedOut);
            this._isAttemptingAutoLogin = false;
        };
        this.attemptAutoLogin = () => {
            if (this._isAttemptingAutoLogin === true) {
                return;
            }
            this._debug("Attempting auto-login");
            createTimeout((cancel) => __awaiter(this, void 0, void 0, function* () {
                try {
                    if (this.autoLoginFunction == null) {
                        return this._debug("No auto-login function set");
                    }
                    const res = yield this.autoLoginFunction();
                    cancel();
                    if (res != null) {
                        this.login(res).then(() => {
                            this._debug("Auto login successful");
                            void this.client.handleOutboxFlushRequest();
                        }).catch((err) => {
                            this.handleAutoLoginFailure(err);
                        });
                    }
                    else {
                        this.handleAutoLoginFailure(new Error("Auto login function returned null"));
                    }
                }
                catch (err) {
                    this.handleAutoLoginFailure(err);
                }
            }), 10000)
                .then(() => {
                this._isAttemptingAutoLogin = false;
            })
                .catch(() => {
                this.handleAutoLoginFailure(new TimeoutError("auto-login"));
            });
        };
        this.handleTransportStatusChange = (status) => {
            if (this.client._closed === true)
                return;
            if (status !== TransportStatus.connected) {
                this.logout();
            }
            if (status === TransportStatus.connected) {
                this.attemptAutoLogin();
            }
        };
        this.useAuthStatus = () => {
            const [v, s] = React.useState(this.status);
            React.useEffect(() => {
                const loginHandler = () => s(AuthExtensionStatus.loggedIn);
                const loggingInHandler = () => s(AuthExtensionStatus.loggingIn);
                const logoutHandler = () => s(AuthExtensionStatus.loggedOut);
                this.emitter.on(AuthExtensionStatus.loggedIn, loginHandler);
                this.emitter.on(AuthExtensionStatus.loggingIn, loggingInHandler);
                this.emitter.on(AuthExtensionStatus.loggedOut, logoutHandler);
                return () => {
                    this.emitter.off(AuthExtensionStatus.loggedIn, loginHandler);
                    this.emitter.off(AuthExtensionStatus.loggingIn, loggingInHandler);
                    this.emitter.off(AuthExtensionStatus.loggedOut, logoutHandler);
                };
            }, []);
            return v;
        };
        this.useAuthState = () => {
            const [v, s] = React.useState(this.currentAuth);
            React.useEffect(() => {
                const loginHandler = (a) => s(a);
                const logoutHandler = () => s(null);
                this.emitter.on(AuthExtensionStatus.loggedIn, loginHandler);
                this.emitter.on(AuthExtensionStatus.loggedOut, logoutHandler);
                return () => {
                    this.emitter.off(AuthExtensionStatus.loggedIn, loginHandler);
                    this.emitter.off(AuthExtensionStatus.loggedOut, logoutHandler);
                };
            }, []);
            return v;
        };
        /**
         * The function you pass should be useCallback'd
         */
        this.useOnAuthError = (fn) => {
            React.useEffect(() => {
                this.client.incomingMessageEmitter.on(MoopsyRawServerToClientMessageEventEnum.AUTH_ERROR, fn);
                return () => {
                    this.client.incomingMessageEmitter.off(MoopsyRawServerToClientMessageEventEnum.AUTH_ERROR, fn);
                };
            }, [fn]);
        };
        this.onAutoLoginFailure = (fn) => {
            this.emitter.on("auto-login-failure", fn);
        };
        this.on = {
            authSuccess: (fn) => {
                this.client.incomingMessageEmitter.on(MoopsyRawServerToClientMessageEventEnum.AUTH_SUCCESS, fn);
            },
            authError: (fn) => {
                this.client.incomingMessageEmitter.on(MoopsyRawServerToClientMessageEventEnum.AUTH_ERROR, fn);
            }
        };
        this.off = {
            authSuccess: (fn) => {
                this.client.incomingMessageEmitter.off(MoopsyRawServerToClientMessageEventEnum.AUTH_SUCCESS, fn);
            },
            authError: (fn) => {
                this.client.incomingMessageEmitter.off(MoopsyRawServerToClientMessageEventEnum.AUTH_ERROR, fn);
            }
        };
        this.client._authExtensionSlot = this;
        this.autoLoginFunction = opts.autoLoginFunction;
        this.status = this.autoLoginFunction == null ? AuthExtensionStatus.loggedOut : AuthExtensionStatus.loggingIn;
        this.client.incomingMessageEmitter.on(MoopsyRawServerToClientMessageEventEnum.AUTH_SUCCESS, this.handleLoginEvent);
        this.client.on.transportStatusChange(this.handleTransportStatusChange);
        if (this.client.getTransportStatus() === TransportStatus.connected) {
            this.attemptAutoLogin();
        }
    }
}
